import type { I_Brand } from '@enable-tech/shared-types/modules/brand/backend/responses/schemas';
import type {
  E_LoyaltyCardStatus,
  E_LoyaltyStatus,
  E_WalletApp,
} from '@enable-tech/shared-types/modules/loyaltyProgram/common/backend/enums';
import type { I_LoyaltyProgramConfig } from '@enable-tech/shared-types/modules/loyaltyProgram/common/backend/responses/schemas';

import type { I_PlatformHandlers } from '@/types/Platforms';

import type { T_DeviceData } from './Device';
import type { E_MaximumUsageType } from './RegisteredCustomer';

export type T_ShopifyCart = {
  token: string;
  item_count: number;
  total_price: number;
  total_discount: number;
  items: T_ShopifyCartItem[];
  discount_codes: Array<{
    code: string;
    applicable: boolean;
  }>;
};

type T_ShopifyCartItem = {
  id: number;
  title: string;
  quantity: number;
  discounted_price: number;
  price: number;
  total_discount: number;
  variant_id: number;
  product_id: number;
  vendor: string;
  url: string;
  image: string;
};

export interface I_ShopifyRoutes {
  root?: string;
}

export interface I_Shopify extends I_PlatformHandlers {
  routes?: I_ShopifyRoutes;
}

export type T_RegisterCustomerBodyParams = {
  phoneNumber: string;
  countryCode: string;
  language?: E_PreferredLanguage;
  title?: E_CustomerTitle;
  birthDate?: string;
  firstName?: string;
  email?: string;
  lastName?: string;
  walletApp?: E_WalletApp;
  deviceData?: T_DeviceData;
  nationality?: string;
};

export type T_RegisterCustomerResponse = {
  success: boolean;
  message: string;
  timestamp: string;
  data: {
    isNewCustomer: boolean;
    loyaltyRecordId: string;
    customerId: string;
    enableId: string;
    loyaltyCardStatus: E_LoyaltyCardStatus;
    loyaltyStatus: E_LoyaltyStatus;
  };
};

export type T_ConfigureWalletPassURLBodyParams = {
  walletApp: E_WalletApp;
};

export type T_ConfigureWalletPassURLResponse = {
  success: boolean;
  data: {
    customerId: string;
    passLink: string;
    walletApp: E_WalletApp;
  };
  timestamp: string;
};

export type T_RedeemptionBodyParams = {
  customerId: string;
  pointsAmount: number;
};

export type T_CouponRedeemptionBodyParams = {
  customerId: string;
  couponId: string;
};

export type T_RedeemptionResponse = {
  success: boolean;
  data: {
    benefit: {
      benefitId: string;
      pointsDeducted: number;
      newPointsBalance: number;
      benefitValue: number;
      currency: string;
      benefit: {
        _id: string;
        companyId: string;
        titleEn: string;
        titleAr: string;
        value: number;
        type: E_RedeemptionBenefitType;
        appliedTo: E_RedeemptionAppliedTo;
        config: {
          maximumUsage?: number;
          maximumUsageType?: E_MaximumUsageType;
          preserveUsage?: boolean;
          expiryDate?: string;
          minimumAmountToRedeem?: number;
        };
        source: E_RedeemptionSource;
        earnedAt: string;
        numberOfUsages: number;
        redeemedPoints?: number;
      };
    };
    shopifyDiscount?: {
      discountCodeId: string;
      discountCode: string;
      discountNodeId?: string;
    };
    shopifyDiscounts?: Array<{
      discountCodeId: string;
      discountCode: string;
      discountNodeId?: string;
    }>;
    discountMapping: {
      mappingId: string;
      discountCode: string;
      title: string;
      remainingUses: number;
      expiresAt?: string;
    };
  };
  message?: string;
};

export enum E_RedeemptionBenefitType {
  FIXED_DISCOUNT = 'fixed_discount',
  PERCENTAGE_DISCOUNT = 'percentage_discount',
}

export enum E_RedeemptionAppliedTo {
  ORDER_INVOICED_AMOUNT = 'order_invoiced_amount',
  MENU_ITEM = 'menu_item',
}

export enum E_RedeemptionSource {
  FLEXIBLE_POINTS = 'flexible_points',
  COUPON_PROGRAM = 'coupon_program',
  TIER_BENEFIT = 'tier_benefit',
}

export enum E_PreferredLanguage {
  ENGLISH = 'english',
  ARABIC = 'arabic',
}

export enum E_CustomerTitle {
  MR = 'Mr.',
  // MRS = 'Mrs.', // this removal is aligned with product team as they requested it to be like LRP (REF: https://teams.microsoft.com/l/message/19:59c95c2062b44267987ee1c5bdfaacdf@thread.tacv2/1771842364942?tenantId=75eb8419-1ce9-49f2-9df2-13ef7b156848&groupId=d32e2225-e649-4399-8db1-4e284a0cc6f0&parentMessageId=1771840444108&teamName=Enable%20-%20Dev%20%2B%20Product&channelName=Product&createdTime=1771842364942)
  MS = 'Ms.',
}

export enum E_EnableLoyaltyProgramName {
  TIERS_PROGRAM = 'Loyalty Tiers',
  COUPONS_PROGRAM = 'Loyalty Coupons',
  POINTS_PROGRAM = 'Loyalty Points',
  PUNCH_CARDS_PROGRAM = 'Loyalty Punch Cards',
}

export type T_EnableLoyaltyProgram = {
  name: E_EnableLoyaltyProgramName;
  description: string;
};

export type T_ShopLoyaltyConfigResponse = {
  loyaltyConfig: I_LoyaltyProgramConfig;
  loyaltyPrograms: T_EnableLoyaltyProgram[];
  brands: I_Brand[];
  countryCode: string;
  countryAcronym: string;
};
